import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { DISPLAY_FONT, makeThemedStyles, useTheme } from '../v10/theme';
import { GlassCard } from '../v10/ui';
import type { Sector, Stage } from '../domain/olmps';
import { FloSnapshot, getFloSnapshot } from '../lib/flo';
import { CompassView } from './CompassView';
import { ToolHost } from './ToolHost';

// Guided-session wrapper — see docs/OLMPS_SYSTEM.md "The daily loop". No writes of
// its own: each step mounts the real tool (which owns its own persistence).
const DUMMY_SECTOR: Sector = 'H';
const DUMMY_STAGE: Stage = 'T';

const MORNING_STEP_COUNT = 4;
const NIGHT_STEP_COUNT = 4;

function FloLine() {
  const { t } = useTheme();
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;
  const [flo, setFlo] = useState<FloSnapshot | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const snap = await getFloSnapshot(supabase, userId);
        if (!cancelled) setFlo(snap);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return (
    <View style={styles.floLine}>
      {loading ? (
        <ActivityIndicator color={t.goldBright} />
      ) : flo ? (
        <Text style={styles.floLineText}>
          Charge {flo.charge.toFixed(1)} · Load {Math.round(flo.load)} · Flow {Math.round(flo.flow.score)}
          {flo.mode === 'throttled' ? ' · Throttled' : ''}
        </Text>
      ) : (
        <Text style={styles.floLineText}>FLO unavailable</Text>
      )}
    </View>
  );
}

export function SessionFlow({
  kind,
  onDone,
  onOpenTool,
}: {
  kind: 'morning' | 'night';
  onDone: () => void;
  onOpenTool: (toolId: string) => void;
}) {
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;
  const [stepIndex, setStepIndex] = useState(0);
  const stepCount = kind === 'morning' ? MORNING_STEP_COUNT : NIGHT_STEP_COUNT;
  const isLast = stepIndex === stepCount - 1;

  const next = () => {
    if (isLast) onDone();
    else setStepIndex((i) => Math.min(stepCount - 1, i + 1));
  };

  let body: React.ReactNode;
  if (kind === 'morning') {
    // V9 Morning Session: Orient / Regulate / Direct
    // Step 1: CLIO (mantra, journal, epic)
    // Step 2: ASTRA (is anything sliding off?)
    // Step 3: NUMA (optional review)
    // Step 4: METIS (verify/adjust day's tasks)
    if (stepIndex === 0) {
      // CLIO: mantra, journal, epic
      body = <ToolHost toolId="epic" sector={DUMMY_SECTOR} stage={DUMMY_STAGE} gear="orient" />;
    } else if (stepIndex === 1) {
      // ASTRA: is anything sliding off?
      body = <CompassView userId={userId} onOpenTool={onOpenTool} />;
    } else if (stepIndex === 2) {
      // NUMA: optional review / FLO stats
      body = (
        <View style={styles.numaWrap}>
          <FloLine />
          <View style={styles.numaBody}>
            <ToolHost toolId="kyrs" sector={DUMMY_SECTOR} stage={DUMMY_STAGE} gear="direct" />
          </View>
        </View>
      );
    } else {
      // METIS: verify/add/shift/reschedule the day's tasks
      body = <ToolHost toolId="krnos" sector={DUMMY_SECTOR} stage={DUMMY_STAGE} gear="direct" />;
    }
  } else {
    // V9 Night Session: Revise
    // Step 1: CLIO (journal)
    // Step 2: ASTRA (completion checklist / close)
    // Step 3: METIS (complete/reschedule tasks)
    // Step 4: NUMA (optional review)
    if (stepIndex === 0) {
      // CLIO: journal
      body = <ToolHost toolId="clio" sector={DUMMY_SECTOR} stage={DUMMY_STAGE} gear="revise" />;
    } else if (stepIndex === 1) {
      // ASTRA: completion checklist / close
      body = (
        <View style={styles.checklistWrap}>
          <GlassCard style={styles.checklistCard}>
            <Text style={styles.checklistTitle}>Close the day</Text>
            <Text style={styles.checklistLine}>Everything ticked or rescheduled?</Text>
            <Text style={styles.checklistLine}>Nothing unscheduled?</Text>
            <Text style={styles.checklistRule}>
              Go to bed with nothing out of place, nothing unscheduled.
            </Text>
          </GlassCard>
        </View>
      );
    } else if (stepIndex === 2) {
      // METIS: complete/reschedule tasks
      body = <ToolHost toolId="krnos" sector={DUMMY_SECTOR} stage={DUMMY_STAGE} gear="revise" />;
    } else {
      // NUMA: optional review / FLO stats
      body = (
        <View style={styles.numaWrap}>
          <FloLine />
          <View style={styles.numaBody}>
            <ToolHost toolId="kyrs" sector={DUMMY_SECTOR} stage={DUMMY_STAGE} gear="revise" />
          </View>
        </View>
      );
    }
  }

  return (
    <View style={styles.root}>
      <Pressable style={styles.exitButton} onPress={onDone} hitSlop={10}>
        <Text style={styles.exitButtonText}>✕</Text>
      </Pressable>
      <View style={styles.body}>{body}</View>
      <View style={styles.footer}>
        <Text style={styles.footerLabel}>
          {kind === 'morning' ? 'Morning session' : 'Night session'} · Step {stepIndex + 1} of {stepCount}
        </Text>
        <Pressable style={styles.footerButton} onPress={next}>
          <Text style={styles.footerButtonText}>{isLast ? 'Finish' : 'Next'}</Text>
        </Pressable>
      </View>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    root: { flex: 1 },
    body: { flex: 1 },
    exitButton: {
      position: 'absolute',
      top: 6,
      right: 12,
      zIndex: 10,
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: t.navyRaised,
      borderWidth: 1,
      borderColor: t.navyLine,
    },
    exitButtonText: { fontSize: 15, color: t.ivorySoft, fontWeight: '700' },
    footer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
      backgroundColor: t.glassStrong,
      borderTopWidth: 1,
      borderTopColor: t.navyLine,
    },
    footerLabel: { fontSize: 11, fontWeight: '800', color: t.ivoryFaint, letterSpacing: 1.2, textTransform: 'uppercase' },
    footerButton: {
      backgroundColor: t.gold,
      boxShadow: `0 6px 18px ${t.goldGlow}`,
      borderRadius: 999,
      paddingHorizontal: 22,
      paddingVertical: 11,
    },
    footerButtonText: { fontWeight: '800', color: t.onGold, letterSpacing: 0.4 },

    numaWrap: { flex: 1 },
    floLine: {
      paddingHorizontal: 16,
      paddingVertical: 10,
      backgroundColor: t.glass,
      borderBottomWidth: 1,
      borderBottomColor: t.lineFaint,
    },
    floLineText: { fontWeight: '800', color: t.goldBright, fontSize: 13 },
    numaBody: { flex: 1 },

    checklistWrap: { flex: 1, padding: 16, justifyContent: 'center' },
    checklistCard: {
      borderRadius: 22,
      padding: 20,
      gap: 10,
    },
    checklistTitle: { fontSize: 24, color: t.ivory, fontFamily: DISPLAY_FONT, letterSpacing: 2.5, textTransform: 'uppercase' },
    checklistLine: { fontSize: 14, fontWeight: '700', color: t.ivorySoft },
    checklistRule: {
      marginTop: 8,
      fontSize: 13,
      fontStyle: 'italic',
      color: t.ivorySoft,
      borderLeftWidth: 2,
      borderLeftColor: t.gold,
      paddingLeft: 10,
    },
  }),
);
